import {
    AdjustmentResultEffect,
    AdjustmentSource,
    AdjustmentState,
    AdjustmentTransferMethod,
} from '../types/closing-adjustments.types';

/**
 * The WIRE shapes of the correction-booking routes — REV-21375, `ClosingAdjustmentsController`.
 *
 * ⚠️ These mirror the backend's response types and are NOT the view's row type. The selectors rename
 * them into `AdjustmentRow`; nothing is computed on the way, and nothing is translated — the backend's
 * response mapper is the one place that knows both spellings (the same rule ADR-0009 contract B4 set
 * for the document flow).
 *
 * ⚠️ **No shape here carries a confidence, a reasoning trail or a proposal state**, because the API
 * does not (ADR-0015 §8). `source` IS here — it is stored and served, and the display is the
 * connection point for a later `Agent` variant.
 */

/** An account as a row renders it. */
export interface IAdjustmentAccountRef {
    accountId: string;
    accountNumber: string;
    name: string;
}

/** One row of a list. Amounts in MINOR UNITS — the view formats, it never re-rounds. */
export interface IAdjustmentListItem {
    adjustmentId: string;
    fiscalYear: number;
    state: AdjustmentState;
    source: AdjustmentSource;
    /** What this correction does to the year result — the `P&L` column. Decided by the server. */
    resultEffect: AdjustmentResultEffect;
    text: string;
    /** ISO `YYYY-MM-DD`. */
    date: string;
    debit: IAdjustmentAccountRef;
    credit: IAdjustmentAccountRef;
    owner: IAdjustmentAccountRef | null;
    amountMinor: number;
    currency: string;
    vatCode: string | null;
    remark: string | null;
    transfer: { method: AdjustmentTransferMethod; at: string } | null;
    booked: { journalEntryId: string; at: string } | null;
    ambiguous: boolean;
    /** The reconciliation looked and did not find it in the journal (V2/AC6). A state, not an error. */
    notFound: boolean;
    /** WITHDRAWN. Not a state — the row keeps the state it was withdrawn from. */
    discarded: boolean;
    /** A user id today. The provider resolves it to a display name where it can. */
    enteredBy: string;
    editedBy: string | null;
    /** The backend's answer to "may this still be edited or discarded" (ADR-0015 §7). */
    editable: boolean;
}

export interface IAdjustmentCounts {
    all: number;
    released: number;
    transferred: number;
    booked: number;
    /** The WITHDRAWN corrections — counted apart from `all`, which stays open + transferred + booked. */
    discarded: number;
}

/** The three derived result rows plus the list total — computed by the backend on every read (§11). */
export interface IAdjustmentResultSummary {
    currency: string;
    provisionalResultMinor: number;
    /** SIGNED. */
    resultImpactMinor: number;
    resultAfterAdjustmentsMinor: number;
    adjustmentTotalMinor: number;
    /** How many listed corrections touch an account with no position yet (Q10). */
    unclassifiedAdjustments: number;
}

/** What the transfer control may offer — QUERIED per mandate, never a constant (§3a). */
export interface IAdjustmentTransferOptions {
    canWriteToErp: boolean;
    /** A translation KEY, never vendor prose. */
    reasonKey: string | null;
    /** The connected system, FROM THE DATA. `null` = nothing connected. */
    connected: { vendor: string; displayName: string } | null;
}

export interface IAdjustmentListResponse {
    fiscalYear: number;
    availableFiscalYears: number[];
    /** The closing's balance-sheet date, `YYYY-MM-DD` — the default booking date of a new row. */
    balanceSheetDate: string;
    readOnly: boolean;
    adjustments: IAdjustmentListItem[];
    counts: IAdjustmentCounts;
    /** The ids `Übertragen (n)` sends. From the server's UNFILTERED set — see the reducer. */
    transferableAdjustmentIds: string[];
    result: IAdjustmentResultSummary;
    transferOptions: IAdjustmentTransferOptions;
}

/** The in-account read (REV-21364). No result rows — that surface renders none. */
export interface IAdjustmentAccountListResponse {
    fiscalYear: number;
    availableFiscalYears: number[];
    readOnly: boolean;
    adjustments: IAdjustmentListItem[];
    openCount: number;
}

export interface IAdjustmentCandidate {
    journalEntryId: string;
    date: string;
    description: string | null;
    amountMinor: number;
    currency: string;
    /** `null` today — the normalised journal carries no document number for any vendor. */
    documentNumber: string | null;
}

export interface IAdjustmentDetailResponse {
    adjustment: IAdjustmentListItem;
    /** Non-empty exactly when the correction is ambiguous — the lines the reconciliation FOUND (§4). */
    candidates: IAdjustmentCandidate[];
    transferOptions: IAdjustmentTransferOptions;
}

/** Creating a correction. ⚠️ No `source` — there is one writer and it is the user (§6). */
export interface ICreateAdjustmentRequest {
    /** The booking date, `YYYY-MM-DD`, or `null` to let the server use the balance sheet date. */
    date: string | null;
    text: string;
    debitAccountId: string;
    creditAccountId: string;
    amountMinor: number;
    ownerAccountId: string | null;
    vatCode: string | null;
    remark: string | null;
}

/** Editing an OPEN correction. ⚠️ No `state` and no `booked` — a client cannot set either (§2). */
export interface IEditAdjustmentRequest {
    /** The booking date, `YYYY-MM-DD`. Editable inside the fiscal year (V2/AC3). */
    date?: string;
    text?: string;
    debitAccountId?: string;
    creditAccountId?: string;
    amountMinor?: number;
    ownerAccountId?: string | null;
    vatCode?: string | null;
    remark?: string | null;
}

/** What one position's transfer did. Partial success is a REGULAR outcome (§3). */
export interface IAdjustmentTransferItemResult {
    adjustmentId: string;
    outcome: 'TRANSFERRED' | 'ALREADY_TRANSFERRED' | 'REJECTED' | 'FAILED';
    /** Present for `REJECTED` and `FAILED`. A translation key. */
    reasonKey?: string;
}

export interface IAdjustmentTransferResponse {
    method: AdjustmentTransferMethod;
    items: IAdjustmentTransferItemResult[];
    transferredCount: number;
    transferredTotalMinor: number;
    currency: string;
}
